import { ref, computed, onUnmounted } from 'vue'
import type { Ref } from 'vue'
import type { TreeOption } from 'naive-ui'
import {
  createImportedBodyOwnershipLookupKeys,
} from '@puppycad/cad-core'
import type { GlbHierarchyNode } from '@puppycad/cad-core'
import {
  buildGlbFeatureManagerTree,
  buildGlbFeatureManagerTreeFragments,
  createGlbNodePresentationUpdate,
} from '@/core/feature-tree/glb-feature-manager-tree'
import type {
  GlbFeatureTreeDatumSlotBinding,
  GlbFeatureTreeNodeBinding,
  GlbFeatureTreeObjectKeyBinding,
  GlbFeatureManagerTreeBuildResult,
  GlbFeatureManagerSceneSketchEntry,
} from '@/core/feature-tree/glb-feature-manager-tree'
import {
  FeatureTreePresentationIndex,
  FeatureTreeRuntimeSubtreeProjector,
} from '@/core/feature-tree/presentation'
import {
  FeatureTreeNodeKind,
  FeatureTreeDataSource,
} from '@/hooks/useFeatureTree'
import type { FeatureTreeNodeMeta } from '@/hooks/useFeatureTree'
import { logger } from '@/utils'

/**
 * 供原生设计树刷新路径合并使用的 GLB 子树结果。
 *
 * 设计说明：
 * 1. `subtreeChildren` 仅为要追加到原生 part-root 之下的顶层节点数组；
 * 2. `bodyChildren` 是从 GLB 子树中抽出的叶子实体节点，用于合并到原生
 *    `solid-bodies-folder` 之下，让 GLB 中的实体在「实体(N)」里参与计数；
 * 3. `nodeMapEntries` 由调用方合并（merge）到 featureTreeNodeMap，
 *    而不是 clear 后写入，使 GLB 节点与原生特征/实体绑定可以共存；
 * 4. 该结构由 `buildSubtreeForMerge()` 返回，并且不会触碰全局 store。
 */
export interface GlbFeatureTreeMergeSubtree {
  subtreeChildren: TreeOption[]
  bodyChildren: TreeOption[]
  nodeMapEntries: Map<string, FeatureTreeNodeMeta>
}

/**
 * GLB 设计树绑定索引。
 * 将三种绑定映射汇总，方便上层按 key/objectId 快速查找。
 */
export interface GlbFeatureTreeBindingIndex {
  /** glb 节点绑定：treeKey → 绑定详情 */
  glbNodeByKey: Map<string, GlbFeatureTreeNodeBinding>
  /** 实体(Body)节点绑定：treeKey → 绑定详情 */
  bodyNodeByKey: Map<string, GlbFeatureTreeNodeBinding>
  /** datum-slot 节点绑定：treeKey → 绑定详情 */
  datumSlotByKey: Map<string, GlbFeatureTreeDatumSlotBinding>
  /** objectId → treeKey 反向索引（用于场景选中 ↔ 树节点联动） */
  keyByObjectId: Map<string, string>
  /** bodyBindingId → 实体节点 key 反向索引（用于 `.puppy` 拓扑代理回流） */
  keysByBodyBindingId: Map<string, GlbFeatureTreeBodyBindingKey[]>
  /** 设计树分组 key 集合（不可选中的节点） */
  groupKeys: Set<string>
}

type GlbFeatureTreeBinding = GlbFeatureTreeNodeBinding | GlbFeatureTreeDatumSlotBinding

interface GlbFeatureTreeBodyBindingKey {
  key: string
  assemblyNodeId?: string
}

/**
 * GLB 设计树装配 hook。
 *
 * 职责：
 * 1. 将 GlbHierarchyNode 层级结构转换为 NaiveUI TreeOption 设计树；
 * 2. 调用 buildGlbFeatureManagerTree() 纯函数完成树构建；
 * 3. 管理三层绑定索引：glbNodeBindings、bodyNodeBindings、objectKeyBindings；
 * 4. 提供按 objectId/treeKey 的快速查询方法。
 */
export function useImportedModelFeatureTree() {
  /** 设计树节点数据（直接绑定到 NaiveUI Tree 组件） */
  const featureTreeData = ref<TreeOption[]>([])
  /** 当前选中的树节点 key 列表 */
  const selectedKeys = ref<string[]>([])

  // ---- 非响应式绑定索引 ----
  /** GLB 节点绑定列表 */
  let glbNodeBindings: GlbFeatureTreeNodeBinding[] = []
  /** 实体节点绑定列表 */
  let bodyNodeBindings: GlbFeatureTreeNodeBinding[] = []
  /** datum-slot 节点绑定列表 */
  let datumSlotBindings: GlbFeatureTreeDatumSlotBinding[] = []
  /** objectId → treeKey 映射列表 */
  let objectKeyBindings: GlbFeatureTreeObjectKeyBinding[] = []
  /** 分组 key 列表 */
  let groupKeyList: string[] = []

  /** 绑定索引（按需重建） */
  let cachedBindingIndex: GlbFeatureTreeBindingIndex | null = null
  /** 当前 Hook UI 树的定位索引，只在结构引用替换时重建。 */
  const presentationIndex = new FeatureTreePresentationIndex()
  /** 在稳定装配父节点下替换运行时子树，不重建无关设计树节点。 */
  const runtimeSubtreeProjector = new FeatureTreeRuntimeSubtreeProjector()
  /** 最新一次 merge 提供给 store 的元数据，等待 Worker 期间也须同步展示字段。 */
  let pendingNodeMapEntries: Map<string, FeatureTreeNodeMeta> | null = null

  /**
   * 重建绑定索引。
   */
  function rebuildBindingIndex(): GlbFeatureTreeBindingIndex {
    const glbNodeByKey = new Map<string, GlbFeatureTreeNodeBinding>()
    const bodyNodeByKey = new Map<string, GlbFeatureTreeNodeBinding>()
    const datumSlotByKey = new Map<string, GlbFeatureTreeDatumSlotBinding>()
    const keyByObjectId = new Map<string, string>()
    const keysByBodyBindingId = new Map<string, GlbFeatureTreeBodyBindingKey[]>()
    const groupKeys = new Set<string>()

    for (const binding of glbNodeBindings) {
      glbNodeByKey.set(binding.key, binding)
    }
    for (const binding of bodyNodeBindings) {
      bodyNodeByKey.set(binding.key, binding)
      if (binding.bodyBindingId) {
        createImportedBodyOwnershipLookupKeys(binding.bodyBindingId).forEach((lookupKey) => {
          const entries = keysByBodyBindingId.get(lookupKey) ?? []
          if (!entries.some((entry) => entry.key === binding.key)) {
            entries.push({
              key: binding.key,
              assemblyNodeId: binding.assemblyNodeId,
            })
          }
          keysByBodyBindingId.set(lookupKey, entries)
        })
      }
    }
    for (const binding of datumSlotBindings) {
      datumSlotByKey.set(binding.key, binding)
    }
    for (const binding of objectKeyBindings) {
      keyByObjectId.set(binding.objectId, binding.key)
    }
    for (const key of groupKeyList) {
      groupKeys.add(key)
    }

    const index: GlbFeatureTreeBindingIndex = {
      glbNodeByKey,
      bodyNodeByKey,
      datumSlotByKey,
      keyByObjectId,
      keysByBodyBindingId,
      groupKeys,
    }
    cachedBindingIndex = index
    return index
  }

  /**
   * 获取当前绑定索引（惰性重建）。
   */
  function getBindingIndex(): GlbFeatureTreeBindingIndex {
    if (!cachedBindingIndex) {
      return rebuildBindingIndex()
    }
    return cachedBindingIndex
  }

  /**
   * 根据 GLB 层级结构构建设计树。
   *
   * @param tabId      标签 ID（用于生成 tree key 前缀）。
   * @param hierarchy  GLB 层级根节点。
   * @param rootLabel  可选的根节点显示名称。
   * @param rootIconType 可选的根节点图标类型。
   * @param fileIdByUuidMap 外链零件 uuid → fileId 映射（可选）。
   * @param sceneSketches viewer 当前的草图列表，用于在 GLB 静态树之上注入动态草图节点。
   * @returns 构建结果（树数据 + 绑定列表）。
   */
  function buildTree(
    tabId: string,
    hierarchy: GlbHierarchyNode,
    rootLabel?: string,
    rootIconType?: 'assembly' | 'part' | 'group',
    fileIdByUuidMap?: Map<string, string>,
    sceneSketches?: GlbFeatureManagerSceneSketchEntry[],
  ): GlbFeatureManagerTreeBuildResult {
    pendingNodeMapEntries = null
    const result = buildGlbFeatureManagerTree({
      tabId,
      hierarchy,
      rootLabel,
      rootIconType,
      sceneSketches,
    })

    // 更新本地响应式状态
    featureTreeData.value = result.tree

    // 更新非响应式绑定
    glbNodeBindings = result.glbNodeBindings
    bodyNodeBindings = result.bodyNodeBindings
    datumSlotBindings = result.datumSlotBindings
    objectKeyBindings = result.objectKeyBindings
    groupKeyList = result.groupKeys

    // 使缓存索引失效
    cachedBindingIndex = null
    presentationIndex.clear()

    logger.info(
      `[glb-feature-tree] 设计树构建完成: ${result.glbNodeBindings.length} 个节点, `
      + `${result.bodyNodeBindings.length} 个实体节点, `
      + `${result.datumSlotBindings.length} 个 datum-slot 节点, `
      + `${result.groupKeys.length} 个分组`,
    )

    return result
  }

  /**
   * 把 GLB 树构建结果转换为 FeatureTree 元数据映射。
   *
   * 完整刷新与运行时片段更新必须共用同一转换规则，避免局部刷新遗漏外链文件、
   * bodyBindingId、datum-slot 或高亮目标等交互语义。
   *
   * @param tabId 当前设计树标签 ID。
   * @param result 完整树或树片段的 GLB 构建结果。
   * @param fileIdByUuidMap 外链零件 uuid → fileId 映射。
   * @returns 可直接并入 FeatureTreeStore 的节点元数据映射。
   */
  function createNodeMapEntries(
    tabId: string,
    result: GlbFeatureManagerTreeBuildResult,
    fileIdByUuidMap?: Map<string, string>,
  ): Map<string, FeatureTreeNodeMeta> {
    const nodeMapEntries = new Map<string, FeatureTreeNodeMeta>()
    for (const binding of result.glbNodeBindings) {
      const externalFileId = binding.externalFileUuid
        ? fileIdByUuidMap?.get(binding.externalFileUuid.trim().toLowerCase())
        : undefined
      nodeMapEntries.set(binding.key, {
        kind: FeatureTreeNodeKind.GlbNode,
        source: FeatureTreeDataSource.Glb,
        objectId: binding.selectionObjectId ?? binding.objectId,
        objectType: binding.objectType,
        assemblyNodeId: binding.assemblyNodeId,
        glbNodeRole: binding.glbNodeRole,
        bodyBindingId: binding.bodyBindingId,
        modelBodyId: binding.geometryObjectId,
        modelBodyRevisionId: binding.geometryObjectId,
        glbTabId: tabId,
        glbPath: binding.glbPath,
        glbAssemblyNodeId: binding.assemblyNodeId,
        glbSourceObjectId: binding.objectId,
        displayName: binding.displayName,
        assemblyNodeDisplayName: binding.assemblyNodeDisplayName,
        assemblyNodeInstanceIndex: binding.assemblyNodeInstanceIndex,
        glbExternalFileUuid: binding.externalFileUuid,
        glbExternalFileId: externalFileId,
        glbExternalAssemblyName: binding.externalAssemblyName,
        assemblyFixationState: binding.fixationState,
        canFixOrFloat: binding.canFixOrFloat,
        selectable: binding.selectable,
        highlightTargets: binding.highlightTargets,
      })
    }
    for (const binding of result.bodyNodeBindings) {
      nodeMapEntries.set(binding.key, {
        kind: FeatureTreeNodeKind.GlbNode,
        source: FeatureTreeDataSource.Glb,
        objectId: binding.selectionObjectId ?? binding.objectId,
        objectType: binding.objectType,
        assemblyNodeId: binding.assemblyNodeId,
        glbNodeRole: binding.glbNodeRole,
        bodyBindingId: binding.bodyBindingId,
        modelBodyId: binding.geometryObjectId,
        modelBodyRevisionId: binding.geometryObjectId,
        glbTabId: tabId,
        glbPath: binding.glbPath,
        glbSourceObjectId: binding.objectId,
        displayName: binding.displayName,
        selectable: binding.selectable,
        highlightTargets: binding.highlightTargets,
        glbBodyBindingId: binding.bodyBindingId,
      })
    }
    for (const binding of result.datumSlotBindings) {
      nodeMapEntries.set(binding.key, {
        kind: FeatureTreeNodeKind.GlbDatumSlot,
        source: FeatureTreeDataSource.Glb,
        objectId: binding.hostObjectId,
        glbTabId: binding.tabId,
        glbDatumSlot: {
          tabId: binding.tabId,
          hostObjectId: binding.hostObjectId,
          slotKind: binding.slotKind,
        },
        glbDatumSlotKind: binding.slotKind,
        selectable: true,
      })
    }
    for (const key of result.groupKeys) {
      nodeMapEntries.set(key, {
        kind: FeatureTreeNodeKind.Group,
        source: FeatureTreeDataSource.Glb,
      })
    }
    for (const binding of result.sketchNodeBindings) {
      nodeMapEntries.set(binding.key, {
        kind: FeatureTreeNodeKind.Object,
        source: FeatureTreeDataSource.Scene,
        objectId: binding.objectId,
        objectType: 'sketch',
      })
    }
    return nodeMapEntries
  }

  /**
   * 构建一棵可合并进原生设计树的 GLB 子树。
   *
   * 与 `buildTree()` 的区别：
   * 1. 不写入 `featureTreeStore.featureTreeData`，不清空 `featureTreeNodeMap`；
   * 2. 返回 `subtreeChildren` 给 `useFeatureTree.refreshTree()` 追加到 part-root 之下；
   * 3. 同步更新本 hook 内的非响应式绑定（glbNodeBindings 等），使后续 objectId 联动、
   *    body 绑定查询仍然基于最新的子树构建结果。
   *
   * 这样原生投影与 GLB 层级可以在同一棵设计树中共存，而不是互相覆盖。
   */
  function buildSubtreeForMerge(
    tabId: string,
    hierarchy: GlbHierarchyNode,
    rootLabel?: string,
    rootIconType?: 'assembly' | 'part' | 'group',
    fileIdByUuidMap?: Map<string, string>,
    sceneSketches?: GlbFeatureManagerSceneSketchEntry[],
  ): GlbFeatureTreeMergeSubtree {
    const result = buildGlbFeatureManagerTree({
      tabId,
      hierarchy,
      rootLabel,
      rootIconType,
      sceneSketches,
    })

    // 同步到本 hook 的非响应式绑定，使 getTreeKeyByObjectId / findBindingByKey
    // 等 API 在原生+GLB 合并模式下继续有效。
    glbNodeBindings = result.glbNodeBindings
    bodyNodeBindings = result.bodyNodeBindings
    datumSlotBindings = result.datumSlotBindings
    objectKeyBindings = result.objectKeyBindings
    groupKeyList = result.groupKeys
    cachedBindingIndex = null
    presentationIndex.clear()
    featureTreeData.value = result.tree

    const nodeMapEntries = createNodeMapEntries(tabId, result, fileIdByUuidMap)

    // 抽取 GLB 子树中的叶子实体节点（objectType === 'body'），
    // 由 `useFeatureTree.refreshTree()` 合并到原生 `solid-bodies-folder` 之下，
    // 这样即便零件标签页跳过 GLB 子树结构合并，实体节点仍然能出现在「实体(N)」里。
    // 排除 body-folder 绑定（它只是组标签，不是可选实体）。
    const bodyLeafKeySet = new Set(
      result.bodyNodeBindings
        .filter((binding) => binding.objectType === 'body')
        .map((binding) => binding.key),
    )
    const bodyChildren: TreeOption[] = []
    const collectBodies = (nodes: readonly TreeOption[]): void => {
      for (const node of nodes) {
        const key = typeof node.key === 'string' ? node.key : String(node.key ?? '')
        if (bodyLeafKeySet.has(key)) {
          bodyChildren.push(node)
        }
        if (Array.isArray(node.children)) {
          collectBodies(node.children as readonly TreeOption[])
        }
      }
    }
    collectBodies(result.tree)

    pendingNodeMapEntries = nodeMapEntries
    return {
      subtreeChildren: result.tree,
      bodyChildren,
      nodeMapEntries,
    }
  }

  /**
   * 构建并应用稳定 GLB 父节点下的运行时子树替换。
   *
   * 该入口同时更新 Hook 自身的树投影、绑定索引与待提交元数据，并返回同一份纯数据更新
   * 供 FeatureTreeStore 应用。这样线性阵列提交与 Undo/Redo 不再请求 Worker 或重建整棵树。
   *
   * @param tabId 当前设计树标签 ID。
   * @param parentHierarchy 运行时子树所在的稳定父 hierarchy 节点。
   * @param removedHierarchyNodes 本次需要替换的旧直接子树根。
   * @param nextHierarchyNodes 本次追加的新直接子树根。
   * @param fileIdByUuidMap 外链零件 uuid → fileId 映射。
   * @param nextHierarchyInsertionIndexByObjectId 新子树根在最终父 children 中的精确索引。
   * @returns 可直接交给 FeatureTreeStore 的运行时子树更新。
   */
  function buildRuntimeSubtreeUpdate(
    tabId: string,
    parentHierarchy: GlbHierarchyNode,
    removedHierarchyNodes: readonly GlbHierarchyNode[],
    nextHierarchyNodes: readonly GlbHierarchyNode[],
    fileIdByUuidMap?: Map<string, string>,
    nextHierarchyInsertionIndexByObjectId?: ReadonlyMap<string, number>,
  ): GlbRuntimeSubtreeUpdate<FeatureTreeNodeMeta> {
    const removedRootKeys = removedHierarchyNodes.map(
      (node) => createGlbNodePresentationUpdate(tabId, node).key,
    )
    const removedHierarchyKeys = new Set<string>()
    const pendingHierarchyNodes = [...removedHierarchyNodes]
    while (pendingHierarchyNodes.length > 0) {
      const hierarchyNode = pendingHierarchyNodes.pop()!
      removedHierarchyKeys.add(createGlbNodePresentationUpdate(tabId, hierarchyNode).key)
      for (let index = hierarchyNode.children.length - 1; index >= 0; index -= 1) {
        pendingHierarchyNodes.push(hierarchyNode.children[index])
      }
    }

    /**
     * 系统文件夹、datum-slot 与虚拟 body 节点均以语义节点 key 为前缀。
     * 这里沿 `::` 边界回溯祖先，只删除属于旧运行时 hierarchy 的派生节点。
     */
    const removedTreeKeys = new Set(removedHierarchyKeys)
    const derivedTreeKeyCandidates = [
      ...bodyNodeBindings.map((binding) => binding.key),
      ...datumSlotBindings.map((binding) => binding.key),
      ...groupKeyList,
      ...objectKeyBindings.map((binding) => binding.key),
    ]
    for (const treeKey of derivedTreeKeyCandidates) {
      let ancestorKey = treeKey
      while (ancestorKey.length > 0) {
        if (removedHierarchyKeys.has(ancestorKey)) {
          removedTreeKeys.add(treeKey)
          break
        }
        const separatorIndex = ancestorKey.lastIndexOf('::')
        if (separatorIndex < 0) break
        ancestorKey = ancestorKey.slice(0, separatorIndex)
      }
    }

    const fragment = buildGlbFeatureManagerTreeFragments(tabId, nextHierarchyNodes)
    const nodeMapEntries = createNodeMapEntries(tabId, fragment, fileIdByUuidMap)
    const fragmentRootByKey = new Map(
      fragment.tree.map(node => [String(node.key), node]),
    )
    const childInsertions = nextHierarchyInsertionIndexByObjectId
      ? nextHierarchyNodes.map((hierarchyNode) => {
          const treeKey = createGlbNodePresentationUpdate(tabId, hierarchyNode).key
          const child = fragmentRootByKey.get(treeKey)
          const index = nextHierarchyInsertionIndexByObjectId.get(hierarchyNode.objectId)
          return child && index !== undefined ? { index, child } : null
        }).filter((entry): entry is GlbRuntimeSubtreeChildInsertion => Boolean(entry))
      : []

    // 先移除旧运行时绑定，再追加片段绑定；装配加载时建立的其他绑定保持原引用和原顺序。
    glbNodeBindings = [
      ...glbNodeBindings.filter((binding) => !removedTreeKeys.has(binding.key)),
      ...fragment.glbNodeBindings,
    ]
    bodyNodeBindings = [
      ...bodyNodeBindings.filter((binding) => !removedTreeKeys.has(binding.key)),
      ...fragment.bodyNodeBindings,
    ]
    datumSlotBindings = [
      ...datumSlotBindings.filter((binding) => !removedTreeKeys.has(binding.key)),
      ...fragment.datumSlotBindings,
    ]
    objectKeyBindings = [
      ...objectKeyBindings.filter((binding) => !removedTreeKeys.has(binding.key)),
      ...fragment.objectKeyBindings,
    ]
    groupKeyList = [
      ...groupKeyList.filter((key) => !removedTreeKeys.has(key)),
      ...fragment.groupKeys,
    ]

    const update: GlbRuntimeSubtreeUpdate<FeatureTreeNodeMeta> = {
      parentKey: createGlbNodePresentationUpdate(tabId, parentHierarchy).key,
      removedRootKeys,
      removedNodeKeys: [...removedTreeKeys],
      children: fragment.tree,
      ...(childInsertions.length === fragment.tree.length && childInsertions.length > 0
        ? { childInsertions }
        : {}),
      nodeMapEntries,
    }
    const targetNodeMap = pendingNodeMapEntries ?? new Map<string, FeatureTreeNodeMeta>()
    const applied = runtimeSubtreeProjector.apply(
      featureTreeData.value,
      targetNodeMap,
      presentationIndex,
      update,
    )
    pendingNodeMapEntries = targetNodeMap
    if (!applied) presentationIndex.clear()

    // 若某个旧实例没有在新片段中重建，只清理该实例的本地选择，不影响其他稳定选择。
    selectedKeys.value = selectedKeys.value.filter(
      (key) => !removedTreeKeys.has(key) || nodeMapEntries.has(key),
    )
    cachedBindingIndex = null
    return update
  }

  /**
   * 追加一棵子树的构建结果到当前设计树。
   * 用于装配体场景下多个零件/子装配的设计树合并。
   * 子树会作为当前根节点的子节点追加，而不是作为同级。
   *
   * @param tabId 追加节点所属的 tab ID。
   * @param result 要追加的构建结果。
   * @param fileIdByUuidMap 外链零件 uuid → fileId 映射（可选）。
   */
  function appendBuildResult(
    tabId: string,
    result: GlbFeatureManagerTreeBuildResult,
    fileIdByUuidMap?: Map<string, string>,
  ): void {
    if (result.tree.length === 0) {
      return
    }
    const existingRoot = featureTreeData.value[0]
    if (existingRoot) {
      const existingChildren = Array.isArray(existingRoot.children)
        ? existingRoot.children
        : []
      const updatedRoot: TreeOption = {
        ...existingRoot,
        children: [...existingChildren, ...result.tree],
        isLeaf: false,
      }
      featureTreeData.value = [updatedRoot, ...featureTreeData.value.slice(1)]
    } else {
      // 兜底：没有现有根时，直接作为顶层节点
      featureTreeData.value = [...result.tree]
    }
    glbNodeBindings = [...glbNodeBindings, ...result.glbNodeBindings]
    bodyNodeBindings = [...bodyNodeBindings, ...result.bodyNodeBindings]
    datumSlotBindings = [...datumSlotBindings, ...result.datumSlotBindings]
    objectKeyBindings = [...objectKeyBindings, ...result.objectKeyBindings]
    groupKeyList = [...groupKeyList, ...result.groupKeys]
    cachedBindingIndex = null
    presentationIndex.clear()

    logger.info(
      `[glb-feature-tree] 追加子树: ${result.glbNodeBindings.length} 个节点, `
      + `${result.bodyNodeBindings.length} 个实体节点, `
      + `${result.datumSlotBindings.length} 个 datum-slot 节点, `
      + `${result.groupKeys.length} 个分组`,
    )
  }

  /**
   * 通过 tree key 查找 GLB 节点绑定。
   */
  function getGlbNodeBindingByKey(key: string): GlbFeatureTreeNodeBinding | undefined {
    return getBindingIndex().glbNodeByKey.get(key)
  }

  /**
   * 通过 tree key 查找实体节点绑定。
   */
  function getBodyNodeBindingByKey(key: string): GlbFeatureTreeNodeBinding | undefined {
    return getBindingIndex().bodyNodeByKey.get(key)
  }

  /**
   * 通过 objectId 查找对应的 tree key。
   * 用于场景选中 → 树节点联动。
   */
  function getTreeKeyByObjectId(objectId: string): string | undefined {
    return getBindingIndex().keyByObjectId.get(objectId)
  }

  /**
   * 通过 imported bodyBindingId 查找对应的实体节点 key。
   *
   * 设计说明：
   * 1. `.puppy` 导入后的 Body 节点是语义节点，不保证有独立 Object3D；
   * 2. 视口点/线/面代理回流时应优先使用 bodyBindingId，而不是退回到父 selection proxy；
   * 3. 同一个 bodyBindingId 在装配实例中出现多次时必须按 assemblyNodeId 消歧，无法唯一命中则不回流。
   *
   * @param bodyBindingId 稳定或运行时 bodyBindingId。
   * @param assemblyNodeId 可选装配节点 ID。
   * @returns 设计树实体节点 key；无法唯一定位时返回 undefined。
   */
  function getTreeKeyByBodyBindingId(
    bodyBindingId: string,
    assemblyNodeId?: string | null,
  ): string | undefined {
    const index = getBindingIndex()
    const entries = createImportedBodyOwnershipLookupKeys(bodyBindingId)
      .flatMap((lookupKey) => index.keysByBodyBindingId.get(lookupKey) ?? [])
      .filter((entry, entryIndex, allEntries) => (
        allEntries.findIndex((candidate) => candidate.key === entry.key) === entryIndex
      ))
    if (entries.length === 0) {
      return undefined
    }

    const normalizedAssemblyNodeId = assemblyNodeId?.trim() ?? ''
    if (normalizedAssemblyNodeId.length > 0) {
      const matchedEntries = entries.filter((entry) => entry.assemblyNodeId === normalizedAssemblyNodeId)
      if (matchedEntries.length === 1) {
        return matchedEntries[0]?.key
      }
    }

    return entries.length === 1 ? entries[0]?.key : undefined
  }

  /**
   * 判断 tree key 是否为分组节点（不可选中）。
   */
  function isGroupKey(key: string): boolean {
    return getBindingIndex().groupKeys.has(key)
  }

  /**
   * 通过 tree key 查找绑定（先查 glbNode，再查 bodyNode）。
   */
  function findBindingByKey(key: string): GlbFeatureTreeBinding | undefined {
    const index = getBindingIndex()
    return index.glbNodeByKey.get(key)
      ?? index.bodyNodeByKey.get(key)
      ?? index.datumSlotByKey.get(key)
  }

  /**
   * 同步 SDK 已确认变化的展示字段，不重建 GLB 子树、绑定或选择状态。
   * @param tabId 更新归属标签。
   * @param nodes 固定字段实际改变的层级节点。
   * @returns 可交给当前激活树应用的相同展示更新。
   */
  function updateHierarchyNodePresentations(
    tabId: string,
    nodes: readonly GlbHierarchyNode[],
  ): GlbTreeNodePresentationUpdate[] {
    if (nodes.length === 0) return []
    const index = getBindingIndex()
    return nodes.map((node) => {
      const update = createGlbNodePresentationUpdate(tabId, node)
      const binding = index.glbNodeByKey.get(update.key)
      if (binding) {
        binding.displayName = update.displayName
        binding.fixationState = update.fixationState
        binding.canFixOrFloat = update.canFixOrFloat
      }
      const treeNode = presentationIndex.resolve(featureTreeData.value, update.key)
      if (treeNode) treeNode.label = update.displayName
      // merge 的节点元数据是独立纯数据，必须与 Hook 绑定一起更新，防止晚到提交还原旧前缀。
      const pendingMeta = pendingNodeMapEntries?.get(update.key)
      if (pendingMeta) {
        pendingMeta.displayName = update.displayName
        pendingMeta.assemblyFixationState = update.fixationState
        pendingMeta.canFixOrFloat = update.canFixOrFloat
      }
      return update
    })
  }

  /**
   * 清空设计树与所有绑定。
   */
  function clearTree(): void {
    featureTreeData.value = []
    selectedKeys.value = []
    glbNodeBindings = []
    bodyNodeBindings = []
    datumSlotBindings = []
    objectKeyBindings = []
    groupKeyList = []
    cachedBindingIndex = null
    presentationIndex.clear()
    pendingNodeMapEntries = null
  }

  onUnmounted(() => {
    clearTree()
  })

  return {
    featureTreeData,
    selectedKeys,
    buildTree,
    buildSubtreeForMerge,
    buildRuntimeSubtreeUpdate,
    appendBuildResult,
    getBindingIndex,
    getGlbNodeBindingByKey,
    getBodyNodeBindingByKey,
    getTreeKeyByObjectId,
    getTreeKeyByBodyBindingId,
    isGroupKey,
    findBindingByKey,
    updateHierarchyNodePresentations,
    clearTree,
  }
}
